import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Route,
  MapPinned,
  Bell,
  Gauge,
  Car,
  KeyRound,
  Plug,
  Zap,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  BarChart3,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import MainNavbar from '../../components/layout/MainNavbar';

// Image URLs - replace with your own when ready
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800&auto=format&fit=crop&q=80',
  realTime: '/wink_device_image.jpeg',
  smartDriving: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80',
  installation: '/info_wink.jpeg',
  whyChoose: 'https://images.unsplash.com/photo-1449965404239-e8e4e2d2cceb?w=800&auto=format&fit=crop&q=80',
};

const DeviceWink = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const realTimeFeatures = [
    { icon: MapPin, label: 'Live location tracking' },
    { icon: Route, label: 'Trip history & route playback' },
    { icon: MapPinned, label: 'Geo-fencing alerts' },
    { icon: Bell, label: 'Instant over-speed notifications' },
  ];

  const smartDrivingFeatures = [
    { icon: Zap, label: 'Harsh acceleration & braking alerts' },
    { icon: Gauge, label: 'Speed monitoring' },
    { icon: KeyRound, label: 'Ignition ON/OFF updates' },
    { icon: BarChart3, label: 'Vehicle health data (OBD diagnostics support)' },
  ];

  const installationFeatures = [
    'Plug directly into the OBD-II port',
    'Auto power on with ignition',
    'Works seamlessly with the TrackYourWheelz portal',
    'Compact & discreet design',
  ];

  const whyChoose = [
    'No installation charges',
    'No vehicle wiring modifications',
    'Quick activation',
    'Ideal for personal & fleet tracking',
    'Reliable and accurate positioning',
  ];

  return (
    <>
      <style>{`
        .gradient-text-wink {
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #6366f1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass-wink {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .glass-dark-wink {
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .feature-icon-glow {
          box-shadow: 0 0 24px rgba(168, 85, 247, 0.35);
        }
        .img-cover {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }
      `}</style>

      <div
        className={`min-h-screen relative overflow-hidden transition-all duration-500 ${
          theme === 'dark'
            ? 'bg-gradient-to-br from-[#1e1b4b] via-[#2d1b4e] to-[#1e1b4b]'
            : 'bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50'
        }`}
      >
        <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="" />

        {/* Hero with image */}
        <section className="relative z-10 pt-24 pb-12 lg:pt-28 lg:pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate('/home')}
              className={`mb-6 -ml-2 font-medium ${
                theme === 'dark'
                  ? 'text-purple-300 hover:text-white hover:bg-white/10'
                  : 'text-purple-700 hover:text-purple-900 hover:bg-purple-100'
              }`}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left space-y-5 order-2 lg:order-1">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    theme === 'dark'
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'bg-purple-100 text-purple-700 border border-purple-200'
                  }`}
                >
                  <Zap className="h-4 w-4" />
                  Plug & Play GPS Tracker
                </div>
                <h1
                  className={`text-3xl sm:text-4xl md:text-5xl font-black tracking-tight ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  WINK
                </h1>
                <p className="text-xl sm:text-2xl font-bold gradient-text-wink">
                  Drive Smarter. Track Smarter.
                </p>
                <p
                  className={`max-w-xl text-base sm:text-lg leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  A powerful plug-and-play GPS tracker. Plug it into your vehicle’s OBD port and
                  start tracking instantly — no wiring, no technicians, no downtime.
                </p>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Perfect for personal vehicles, family cars, and small fleets.
                </p>
                <Button
                  onClick={() => navigate('/home')}
                  className={`h-12 px-8 text-base font-semibold shadow-lg transition-all duration-300 hover:scale-105 ${
                    theme === 'dark'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                  }`}
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] max-h-[340px] lg:max-h-none">
                  <img
                    src={IMAGES.hero}
                    alt="WINK GPS tracker"
                    className="img-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1603638725135-928baf863eff?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Real-Time Vehicle Tracking - image left */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card
              className={`overflow-hidden border-0 shadow-2xl rounded-2xl md:rounded-3xl p-0 ${
                theme === 'dark' ? 'glass-dark-wink' : 'glass-wink'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <div className="relative w-full lg:w-2/5 min-h-[240px] lg:min-h-[320px] flex-shrink-0">
                  <img
                    src={IMAGES.realTime}
                    alt="Real-time vehicle tracking"
                    className="img-cover w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1603638725135-928baf863eff?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent lg:from-black/40" />
                  <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center feature-icon-glow">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                </div>
                <CardContent className="flex-1 p-6 md:p-8 lg:p-10">
                  <h2
                    className={`text-xl md:text-2xl font-bold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Real-Time Vehicle Tracking
                  </h2>
                  <p
                    className={`text-sm md:text-base mb-6 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Stay connected to your vehicle 24/7 with:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                    {realTimeFeatures.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-3 md:p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                            theme === 'dark'
                              ? 'bg-white/5 hover:bg-white/10 border border-white/10'
                              : 'bg-purple-50/80 hover:bg-purple-100/80 border border-purple-100'
                          }`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span
                            className={`text-sm font-medium ${
                              theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                            }`}
                          >
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>

        {/* Smart Driving Insights - image right */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card
              className={`overflow-hidden border-0 shadow-2xl rounded-2xl md:rounded-3xl p-0 ${
                theme === 'dark' ? 'glass-dark-wink' : 'glass-wink'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <CardContent className="flex-1 p-6 md:p-8 lg:p-10 order-2 lg:order-1">
                  <h2
                    className={`text-xl md:text-2xl font-bold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Smart Driving Insights
                  </h2>
                  <p
                    className={`text-sm md:text-base mb-6 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Drive safer and smarter with:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                    {smartDrivingFeatures.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-3 md:p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                            theme === 'dark'
                              ? 'bg-white/5 hover:bg-white/10 border border-white/10'
                              : 'bg-indigo-50/80 hover:bg-indigo-100/80 border border-indigo-100'
                          }`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span
                            className={`text-sm font-medium ${
                              theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                            }`}
                          >
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
                <div className="relative w-full lg:w-2/5 min-h-[240px] lg:min-h-[320px] flex-shrink-0 order-1 lg:order-2">
                  <img
                    src={IMAGES.smartDriving}
                    alt="Smart driving analytics"
                    className="img-cover w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/50 to-transparent lg:from-black/40" />
                  <div className="absolute bottom-4 right-4 lg:bottom-6 lg:right-6 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center feature-icon-glow">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Effortless Installation - image left */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card
              className={`overflow-hidden border-0 shadow-2xl rounded-2xl md:rounded-3xl p-0 ${
                theme === 'dark' ? 'glass-dark-wink' : 'glass-wink'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <div className="relative w-full lg:w-2/5 min-h-[240px] lg:min-h-[300px] flex-shrink-0">
                  <img
                    src={IMAGES.installation}
                    alt="Easy OBD installation"
                    className="img-cover w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent lg:from-black/40" />
                  <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center feature-icon-glow">
                    <Plug className="w-6 h-6 text-white" />
                  </div>
                </div>
                <CardContent className="flex-1 p-6 md:p-8 lg:p-10">
                  <h2
                    className={`text-xl md:text-2xl font-bold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Effortless Installation
                  </h2>
                  <ul className="space-y-3 md:space-y-4">
                    {installationFeatures.map((text, i) => (
                      <li
                        key={i}
                        className={`flex items-center gap-3 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        <CheckCircle
                          className={`w-5 h-5 flex-shrink-0 ${
                            theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                          }`}
                        />
                        <span className="text-sm md:text-base">{text}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>

        {/* Why Choose WINK - banner image + grid */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8 pb-24">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl mb-10 h-48 sm:h-56 md:h-64">
              <img
                src={IMAGES.whyChoose}
                alt="Why choose WINK"
                className="img-cover w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1449965404239-e8e4e2d2cceb?w=800&auto=format&fit=crop&q=80';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-end">
                <div className="p-6 md:p-8 w-full">
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    Why Choose <span className="text-purple-300">WINK?</span>
                  </h2>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {whyChoose.map((text, i) => (
                <Card
                  key={i}
                  className={`border-0 shadow-xl rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
                    theme === 'dark' ? 'glass-dark-wink' : 'glass-wink'
                  }`}
                >
                  <CardContent className="p-5 md:p-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <span
                      className={`font-medium text-sm md:text-base ${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                      }`}
                    >
                      {text}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button
                onClick={() => navigate('/contact-us')}
                className={`h-12 px-8 text-base font-semibold shadow-lg transition-all duration-300 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                }`}
              >
                Get WINK Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DeviceWink;
